import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProject, removeProjectApplicationCategory } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function ProjectApplicationCategoryListPage() {
  const { businessDomainId, businessUnitId, projectId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetRow, setTargetRow] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchProject(projectId);
      setRows((d.associatedApplicationCategories || []).map((c) => ({ id: c.applicationCategoryId, ...c })));
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeleteConfirm = async () => {
    try {
      await removeProjectApplicationCategory(projectId, targetRow.applicationCategoryId);
      notify.success(t("message.delete.success")); loadData();
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false); setTargetRow(null);
  };

  const columns = [
    { field: "applicationCategoryName", headerName: t("applicationCategory.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: "", width: 60, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error"
          onClick={(e) => { e.stopPropagation(); setTargetRow(p.row); setConfirmOpen(true); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("project.applicationCategory.list.title")}</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/application-categories/add`)}>{t("common.add")}</Button>
      </Box>
      <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
        loading={loading} autoHeight pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>
      <ConfirmDialog open={confirmOpen} onConfirm={handleDeleteConfirm}
        onCancel={() => { setConfirmOpen(false); setTargetRow(null); }} />
    </Box>
  );
}
